import { useState, useEffect, useCallback, useRef } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { CommissionPolicyDTO } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid commission policy': '返佣策略参数无效',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

interface CommissionForm {
  level1FixedFen: number;
  level1Percentage: number;
  level2FixedFen: number;
  level2Percentage: number;
  level3FixedFen: number;
  level3Percentage: number;
}

function dtoToForm(dto: CommissionPolicyDTO): CommissionForm {
  return {
    level1FixedFen: dto.level1_fixed_fen,
    level1Percentage: dto.level1_percentage,
    level2FixedFen: dto.level2_fixed_fen,
    level2Percentage: dto.level2_percentage,
    level3FixedFen: dto.level3_fixed_fen,
    level3Percentage: dto.level3_percentage,
  };
}

const EMPTY_FORM: CommissionForm = {
  level1FixedFen: 0,
  level1Percentage: 0,
  level2FixedFen: 0,
  level2Percentage: 0,
  level3FixedFen: 0,
  level3Percentage: 0,
};

export function useCommissionPolicy(selectedMarketId = 'CN') {
  const requestId = useRef(0);
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<CommissionPolicyDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CommissionForm>(EMPTY_FORM);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getCommissionPolicy(selectedMarketId);
      if (id !== requestId.current) return;
      if (result.market_id !== selectedMarketId) throw new Error('奖励配置地区不匹配');
      setData(result);
      setForm(dtoToForm(result));
    } catch (e) {
      if (id === requestId.current) setError(resolveError(e));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [selectedMarketId]);

  useEffect(() => {
    load();
    return () => { requestId.current++; };
  }, [load]);

  const updateField = useCallback(<K extends keyof CommissionForm>(key: K, value: CommissionForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await settingsService.updateCommissionPolicy({ ...form, market_id: selectedMarketId });
      await load();
      addToast('success', '返佣策略已保存');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  }, [form, selectedMarketId, addToast, load]);

  return { data, loading: loading || (!error && data?.market_id !== selectedMarketId), saving, error, form, updateField, save, reload: load };
}
